import { configureApiClient } from '@kidshome/api-contract';
import { useAuthStore } from '@/entities/session/auth-store';
import { fetchCsrfToken, refresh as refreshRequest } from '@/entities/session/auth-api';

/**
 * Wire the api-contract mutator's auth bridge to the app's auth store. Called once at startup.
 *
 * - `getAccessToken` — the in-memory access token (never persisted).
 * - `getCsrfToken` — the double-submit token. The secret half is an httpOnly cookie the browser
 *   sends automatically; the readable half is RETURNED IN THE BODY of `GET /v1/auth/csrf` and held
 *   in memory (the store), then echoed as `X-CSRF-Token` on cookie routes (`/refresh`, `/logout`,
 *   `/change-password`). The backend signs the token against a STABLE identifier (not the rotating
 *   `rt` cookie), so a single token stays valid across refresh rotations.
 * - `refresh` — single refresh round-trip; on success stores + returns the new access token. `/refresh`
 *   is itself CSRF-protected, so we ensure a token exists before calling it; no post-refresh re-fetch
 *   is needed since the token is not bound to the rotated cookie. The mutator dedupes concurrent
 *   refreshes upstream.
 * - `onAuthFailure` — refresh failed: clear the session (the router redirects to login).
 */
export function installApiClient(): void {
  configureApiClient({
    getAccessToken: () => useAuthStore.getState().accessToken,
    getCsrfToken: () => useAuthStore.getState().csrfToken,
    // Scoped impersonation (ADR-0055): while a platform owner is acting in a household, every request
    // carries `X-Act-As-Household`. The server honors it only for an allowlisted owner (else 403).
    getActAsHouseholdId: () => useAuthStore.getState().actingAs?.householdId ?? null,
    refresh: async () => {
      try {
        // A CSRF token must accompany the (CSRF-protected) refresh call. On the very first refresh
        // there may be none yet (e.g. a cold-load probe before bootstrap fetched one); fetch it.
        if (!useAuthStore.getState().csrfToken) {
          const { csrfToken } = await fetchCsrfToken();
          useAuthStore.getState().setCsrfToken(csrfToken);
        }
        const tokens = await refreshRequest();
        useAuthStore.getState().setTokens(tokens);
        return tokens.accessToken;
      } catch {
        return null;
      }
    },
    onAuthFailure: () => {
      useAuthStore.getState().clear();
    },
  });
}
